// Display surface for the `origin` field on glossary terms.
//
// Three values today (frontend/src/data/types.ts:15):
//   openapi  — seed term lifted directly from an OpenAPI spec
//   llm      — proposed by Haiku from the doc's prose
//   user     — reviewer-authored or reviewer-edited
//
// Every rail row discloses its origin via a short text pill in the
// term header and an attribution footer under the definition. This
// is a product invariant, not a nice-to-have: the seeds-only-vs-Haiku
// demo (PRD 11) collapses without it, and "did mAiSight write this or
// did the API author?" is a question readers will and should ask.
//
// The widget carries a parallel impl in frontend/src/widget/termOrigin.ts.
// Both surfaces use the same data-origin attribute, pill text, and
// footer text so a CSS rule written once styles both.

import type { TermOrigin } from "../data/types";

export const ORIGIN_ATTR = "data-origin";

export interface OriginDisclosure {
  /** Short label rendered inside the pill — 2–5 chars. */
  pillLabel: string;
  /** Sentence rendered under the definition. */
  footerLabel: string;
  /** Tooltip on the pill (also serves the screen-reader label). */
  longLabel: string;
}

const DISCLOSURES: Record<TermOrigin, OriginDisclosure> = {
  openapi: {
    pillLabel: "spec",
    footerLabel: "from OpenAPI spec",
    longLabel: "Lifted from the OpenAPI spec",
  },
  llm: {
    pillLabel: "AI",
    footerLabel: "extracted by mAiSight",
    longLabel: "Extracted from the prose by mAiSight",
  },
  user: {
    pillLabel: "edit",
    footerLabel: "edited by reviewer",
    longLabel: "Reviewer-authored or reviewer-edited",
  },
  extension: {
    pillLabel: "auto",
    footerLabel: "extracted on demand",
    longLabel: "Extracted on demand by the mAiSight browser extension",
  },
};

export function disclose(origin: TermOrigin): OriginDisclosure {
  return DISCLOSURES[origin];
}

/** Ordered for legend rendering — matches the conceptual hierarchy
 *  "structured source → AI proposal → human edit". */
export const ORIGIN_LEGEND_ORDER: ReadonlyArray<TermOrigin> = [
  "openapi",
  "llm",
  "user",
  "extension",
];
